import { LogoLoader, ProgressBar } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import StartedGreyIcon from "components/icons/StartedGreyIcon";
import style from "components/reports/notification-details/notification-details.scss";
import { AssetRegistrationJobDetail, AssetRegistrationJobStatus } from "domain/assetRegistration";
import { Notification } from "domain/notification";
import { assetService, parseAssetRegistrationJobId } from "services/asset-management/AssetService";
import { useTheme } from "utils/useTheme";

import FailedRedNotificationIcon from "assets/images/icons/alert.svg";
import PassedGreenNotificationIcon from "assets/images/icons/checkMarkInCircle.svg";
import WarningYellowNotificationIcon from "assets/images/icons/quickIndeterminate.svg";

import testIds from "testIds.json";

const REFETCH_JOB_DETAILS_TIMER_MILLISECONDS = 5_000;

interface Props {
    refreshCount: number;
    jobDetail?: Notification;
    setRefreshIcon: (show: boolean) => void;
}

const AssetRegistrationNotificationDetails = (props: Props): React.JSX.Element => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [jobDetails, setJobDetails] = React.useState<AssetRegistrationJobDetail>();
    const [showInPercentage, setShowInPercentage] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>();
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const theme = useTheme();

    const fetchData = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        let jobUuid: string;
        try {
            jobUuid = parseAssetRegistrationJobId(props.jobDetail?.data ?? "").jobUuid;
        } catch {
            setLoading(false);
            return;
        }

        assetService
            .fetchAssetRegistrationJob(jobUuid, abortController)
            .then((details: AssetRegistrationJobDetail) => {
                setJobDetails(details);
                const totalProcessed = details.successfulCount + details.failedCount;
                const isRunning = details.status === "IN_PROGRESS";
                setShowInPercentage(
                    isRunning
                        ? details.totalRequestedReports > 0
                            ? Math.floor((totalProcessed * 100) / details.totalRequestedReports)
                            : 0
                        : 100
                );
                props.setRefreshIcon(isRunning);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    props.setRefreshIcon(true);
                    setRequestFailureMessage(t("Notification.assetRegistrationNotifications.requestFailed"));
                }
            })
            .finally(() => setLoading(false));
    };

    React.useEffect(() => {
        setLoading(true);
        fetchData();

        return () => {
            abortControllers.forEach((controller) => controller.abort());
        };
    }, [props.refreshCount]);

    React.useEffect(() => {
        if (jobDetails?.status === "IN_PROGRESS") {
            const timer = setTimeout(() => {
                fetchData();
            }, REFETCH_JOB_DETAILS_TIMER_MILLISECONDS);
            return () => {
                clearTimeout(timer);
            };
        }
    });

    const totalProcessed = (jobDetails?.successfulCount ?? 0) + (jobDetails?.failedCount ?? 0);

    const createDetailsDialog = (jobStatus: AssetRegistrationJobStatus | undefined) => {
        if (typeof jobDetails === "undefined" || typeof jobStatus === "undefined") {
            return null;
        }

        const detailsRecord: Record<AssetRegistrationJobStatus, string> = {
            COMPLETED: t("Notification.assetRegistrationNotifications.successDetails", {
                totalAssetsRegistered: jobDetails.totalAssetsRegistered,
            }),
            COMPLETED_WITH_ERRORS: t("Notification.assetRegistrationNotifications.warningDetails", {
                totalAssetsRegistered: jobDetails.totalAssetsRegistered,
                totalReportsFailed: jobDetails.failedCount,
            }),
            FAILED: t("Notification.assetRegistrationNotifications.failedDetails"),
            CANCELLED: t("Notification.assetRegistrationNotifications.cancelledDetails"),
            IN_PROGRESS: t("Notification.assetRegistrationNotifications.inProgressDetails", {
                totalRequestedReports: jobDetails.totalRequestedReports,
            }),
        };

        const iconRecord: Partial<Record<AssetRegistrationJobStatus, string>> = {
            COMPLETED: PassedGreenNotificationIcon,
            COMPLETED_WITH_ERRORS: WarningYellowNotificationIcon,
            FAILED: FailedRedNotificationIcon,
            CANCELLED: FailedRedNotificationIcon,
        };

        const failedJobUuid = (
            <div>
                <strong>{t("Notification.assetRegistrationNotifications.failedJobUuid")}</strong> {jobDetails.jobUuid}
                <CopyToClipboard value={jobDetails.jobUuid} />
            </div>
        );

        const statusDetails =
            jobStatus === "FAILED" || jobStatus === "CANCELLED" ? (
                <div className={style.details}>
                    {detailsRecord[jobStatus]} {failedJobUuid}
                </div>
            ) : (
                <div className={style.details}>{detailsRecord[jobStatus]}</div>
            );

        const showImage =
            jobStatus === "IN_PROGRESS" ? (
                <StartedGreyIcon key={1} pathColor={theme.contentBackgroundColor} fillColor={theme.textColor} />
            ) : (
                <img className={style.image} src={iconRecord[jobStatus]} alt={t("AltText.confirmationIcon")} />
            );

        const numberOfReportsProcessed = (
            <div data-testid={testIds.workArea.asset.assetRegistrationResultsDialog.numberOfRegisteredAssetsLabel}>
                {t("Notification.assetRegistrationNotifications.numberOfProcessedReports", {
                    totalProcessed,
                    totalRequestedReports: jobDetails.totalRequestedReports,
                })}
            </div>
        );

        return (
            <>
                <div className={style.titleMessage}>{showImage}</div>
                {statusDetails}
                <div className={style.numberInPercent}>
                    {t("Notification.assetRegistrationNotifications.showInPercentage", {
                        percentage: showInPercentage,
                    })}
                </div>
                {numberOfReportsProcessed}
                <ProgressBar
                    progress={showInPercentage}
                    label=""
                    testId={testIds.workArea.asset.assetRegistrationResultsDialog.statusInPercentageLabel}
                    className={style.progressBar}
                />
            </>
        );
    };

    return (
        <div
            data-testid={testIds.workArea.asset.assetRegistrationResultsDialog.itself}
            className={style.detailContainer}
            data-type={props.jobDetail?.type}
        >
            <>
                {loading ? (
                    <LogoLoader />
                ) : requestFailureMessage ? (
                    <div>{requestFailureMessage}</div>
                ) : (
                    createDetailsDialog(jobDetails?.status)
                )}
            </>
        </div>
    );
};

export default AssetRegistrationNotificationDetails;
